<template>
  <v-modal short @close="handleModalDismiss">
    <template :slot="PUSH_NOTIFICATION_STAGES.REQUEST">
      <div class="auth-modal__content pt-2">
        <div>
          <h2 class="mt-1 mb-2">
            {{
              $t("components.modals.notificationPermissionTrigger.requestTitle")
            }}
          </h2>
          <p class="mb-3 mt-0">
            {{
              $t("components.modals.notificationPermissionTrigger.requestText")
            }}
          </p>
          <v-button :is-loading="isLoading" block @click="askForPermissions">
            {{
              $t(
                "components.modals.notificationPermissionTrigger.requestButtonYes"
              )
            }}
          </v-button>
          <v-button block class="mt-2" ghost @click="() => closeModal(true)">
            {{
              $t(
                "components.modals.notificationPermissionTrigger.requestButtonClose"
              )
            }}
          </v-button>
        </div>
      </div>
    </template>
    <template :slot="PUSH_NOTIFICATION_STAGES.GRANTED">
      <div class="text-center px-2 pt-4">
        <v-icon size="3rem" icon="alarm_on"></v-icon>
        <h2 class="my-2">
          {{
            $t("components.modals.notificationPermissionTrigger.grantedTitle")
          }}
        </h2>
        <p class="my-0">
          {{
            $t("components.modals.notificationPermissionTrigger.grantedText")
          }}
        </p>
        <v-button block secondary class="mt-3" @click="() => closeModal()">
          {{
            $t(
              "components.modals.notificationPermissionTrigger.grantedButtonClose"
            )
          }}
        </v-button>
      </div>
    </template>
    <template :slot="PUSH_NOTIFICATION_STAGES.DENIED">
      <div class="text-center px-2 pt-4">
        <v-icon size="3rem" icon="notifications_off"></v-icon>
        <h2 class="my-2">
          {{
            $t("components.modals.notificationPermissionTrigger.deniedTitle")
          }}
        </h2>
        <p>
          {{ $t("components.modals.notificationPermissionTrigger.deniedText") }}
        </p>
        <v-button v-if="isNativePush" primary block @click="openSettings">
          {{
            $t(
              "components.modals.notificationPermissionTrigger.deniedButtonOpenSetting"
            )
          }}
        </v-button>
      </div>
    </template>
  </v-modal>
</template>

<script>
import { mapMutations, mapState, mapGetters, mapActions } from "vuex"

import { WINDOW_EVENTS } from "@shared/admin-app-constants"

import { ACTIVITY_TYPES } from "@/store/analytics"
import EventBus, { EVENTS } from "@/lib/event-bus"
import {
  initializePushNotifications,
  requestPermission,
  isNativePush,
  openSettings,
  queryPushStatus
} from "@/lib/push-notification"
import isPreviewContext from "@/lib/is-platform/is-preview-context"
import isIframe from "@/lib/is-platform/is-iframe"

import { PUSH_NOTIFICATION_STAGES } from "@/lib/constants"

export default {
  data() {
    return {
      isGranted: false,
      isDenied: false,
      showModal: false,
      isLoading: false,
      isNativePush,
      PUSH_NOTIFICATION_STAGES
    }
  },

  computed: {
    ...mapState("notifications", ["canAsk"]),
    ...mapGetters("notifications", [
      "shouldAskForPermission",
      "isPushEnabled",
      "canAskForPermission",
      "hasAlreadyDismissed"
    ]),

    ...mapGetters("auth", ["isAuthenticated"]),
    ...mapGetters(["app"])
  },

  mounted() {
    EventBus.$on(EVENTS.PROMPT_PUSH_PERMISSION, data => {
      if (
        data?.ignoreDismissed
          ? this.canAskForPermission
          : this.shouldAskForPermission
      ) {
        if (isPreviewContext()) {
          const { postMessage } = require("@/lib/admin-iframe-helpers")

          postMessage(WINDOW_EVENTS.PROMPT_PUSH_PERMISSION)
          this.trackActivity({
            activityType: ACTIVITY_TYPES.PUSH_NOTIFICATION_PROMPTED,
            data: {}
          })
        } else {
          queryPushStatus().then(() => {
            if (!this.isPushEnabled()) {
              this.triggerModal()

              this.trackActivity({
                activityType: ACTIVITY_TYPES.PUSH_NOTIFICATION_PROMPTED,
                data: {}
              })
            }
          })
        }
      }
    })
  },

  methods: {
    ...mapMutations("notifications", [
      "setToken",
      "setLastAskedForPermission",
      "setReceiveRemindersViaPushNotification"
    ]),
    ...mapActions("analytics", ["trackActivity"]),

    triggerModal() {
      this.$router.queue.push(PUSH_NOTIFICATION_STAGES.REQUEST)
    },

    closeModal(track = false) {
      if (track) {
        this.trackActivity({
          activityType: ACTIVITY_TYPES.PUSH_NOTIFICATION_DISMISSED,
          data: {}
        })
      }

      this.$router.removeHash()
      this.handleClose()
    },

    handleModalDismiss() {
      if (!this.isGranted) {
        this.trackActivity({
          activityType: ACTIVITY_TYPES.PUSH_NOTIFICATION_DISMISSED,
          data: {}
        })
      }

      this.handleClose()
    },

    handleClose() {
      this.setLastAskedForPermission()
      this.showModal = false
      this.isDenied = false
      this.isGranted = false
      this.isLoading = false
    },

    askForPermissions() {
      this.isLoading = true

      requestPermission()
        .then(() => {
          this.setReceiveRemindersViaPushNotification(true)

          this.$router.push(PUSH_NOTIFICATION_STAGES.GRANTED)
          this.isGranted = true
        })
        .catch(() => {
          this.$router.push(PUSH_NOTIFICATION_STAGES.DENIED)
          this.isDenied = true
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    openSettings() {
      openSettings()
      this.closeModal()
    }
  },

  watch: {
    isAuthenticated: {
      immediate: true,
      handler: function() {
        if (this.isAuthenticated) {
          initializePushNotifications().then(() => {
            if (
              this.canAskForPermission &&
              !this.hasAlreadyDismissed &&
              !isIframe()
            ) {
              if (!this.isPushEnabled()) {
                this.triggerModal()
              }
            }
          })
        }
      }
    }
  }
}
</script>
